import { useState, type FormEvent } from 'react';
import type { Member, Risk, Status } from './api';

const SCORES = [1, 2, 3, 4, 5];
const STATUSES: Status[] = ['open', 'treating', 'accepted', 'closed'];

export interface RiskInput {
  title: string; description: string; likelihood: number; impact: number;
  status: Status; treatment: string; ownerUserId: string | null;
  residualLikelihood: number | null; residualImpact: number | null;
}

interface Props {
  members: Member[];
  initial?: Risk;
  onSubmit(input: RiskInput): Promise<void>;
  onCancel?(): void;
  serverErrors?: Record<string, string>;
}

/** Client-side checks are UX only; the API is authoritative (it rejects anything outside 1–5). */
export function RiskForm({ members, initial, onSubmit, onCancel, serverErrors = {} }: Props) {
  const [v, setV] = useState<RiskInput>({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    likelihood: initial?.likelihood ?? 3,
    impact: initial?.impact ?? 3,
    status: initial?.status ?? 'open',
    treatment: initial?.treatment ?? '',
    ownerUserId: initial?.ownerUserId ?? null,
    residualLikelihood: initial?.residualLikelihood ?? 0,
    residualImpact: initial?.residualImpact ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (v.title.trim().length < 3) return setLocalError('Title must contain 3–160 characters');
    setLocalError(null);
    setBusy(true);
    try {
      await onSubmit({ ...v, title: v.title.trim() });
    } finally {
      setBusy(false);
    }
  }

  const err = (k: string) => serverErrors[k] && <span className="error" role="alert">{serverErrors[k]}</span>;

  return (
    <form onSubmit={submit} className="risk-form" aria-label={initial ? 'Edit risk' : 'New risk'}>
      <label>Title<input value={v.title} maxLength={160} onChange={(e) => setV({ ...v, title: e.target.value })} /></label>
      {err('title')}
      <label>Description<textarea value={v.description} maxLength={5000} onChange={(e) => setV({ ...v, description: e.target.value })} /></label>
      <div className="row">
        <label>Likelihood
          <select value={v.likelihood} onChange={(e) => setV({ ...v, likelihood: Number(e.target.value) })}>
            {SCORES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Impact
          <select value={v.impact} onChange={(e) => setV({ ...v, impact: Number(e.target.value) })}>
            {SCORES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Status
          <select value={v.status} onChange={(e) => setV({ ...v, status: e.target.value as Status })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Owner
          <select value={v.ownerUserId ?? ''} onChange={(e) => setV({ ...v, ownerUserId: e.target.value || null })}>
            <option value="">Unassigned</option>
            {members.filter((m) => m.role !== 'auditor').map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
          </select>
        </label>
      </div>
      {err('likelihood')}{err('impact')}{err('ownerUserId')}
      <fieldset className="row">
        <legend>Residual risk (after treatment)</legend>
        <label>Residual likelihood
          <select value={v.residualLikelihood ?? 0} onChange={(e) => setV({ ...v, residualLikelihood: Number(e.target.value) })}>
            <option value={0}>Not assessed</option>
            {SCORES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Residual impact
          <select value={v.residualImpact ?? 0} onChange={(e) => setV({ ...v, residualImpact: Number(e.target.value) })}>
            <option value={0}>Not assessed</option>
            {SCORES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </fieldset>
      {err('residualLikelihood')}{err('residualImpact')}
      <label>Treatment<textarea value={v.treatment} maxLength={5000} onChange={(e) => setV({ ...v, treatment: e.target.value })} /></label>
      {localError && <span className="error" role="alert">{localError}</span>}
      <div className="row">
        <button type="submit" disabled={busy}>{initial ? 'Save' : 'Add risk'}</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}
